import { VariableData } from "@/models/Filter";

/**
 * Filters a raw crosstab /search response on both axes before any row ids
 * are assigned: rows (variables) are kept only if their variableLabel is
 * visible in the dataset or already part of the saved analysis; columns
 * (crossVariableLabel, nested per-row in descriptiveMetrics) use the same
 * rule. Must run before transformCrosstabData/extractColumnNValues/
 * getCrosstabColumns so every downstream consumer (preview, rollup options,
 * reference column, save) sees already-reconciled data — row ids are a
 * positional index reassigned on every fetch, so filtering must happen on
 * the raw array, not after ids are attached.
 */
/**
 * Viewer-only guard: restricts a live (dashboard-filter-triggered) /search
 * response to rows/columns that are part of the saved analysis, by label.
 * Unlike reconcileCrosstabVisibility (editor-time, needs current hidden-flag
 * data to decide what's newly ineligible), the viewer has no hidden-flag
 * data and must simply never show more than what was saved — mirrors the
 * same fix already applied to BarChart.tsx/TableChart.tsx's viewer paths.
 */
export function restrictCrosstabToSavedLabels(
  data: VariableData[],
  savedRowLabels: Set<string>,
  savedColumnLabels: Set<string>,
): VariableData[] {
  return data
    .filter((variable) => savedRowLabels.has(variable.variableLabel))
    .map((variable) => ({
      ...variable,
      surveys: variable.surveys.map((survey) => ({
        ...survey,
        descriptiveMetrics: survey.descriptiveMetrics?.filter((metric) =>
          savedColumnLabels.has(metric.crossVariableLabel),
        ),
      })),
    }));
}

export function reconcileCrosstabVisibility(
  data: VariableData[],
  hiddenRowLabels: Map<string, string>,
  hiddenColumnLabels: Map<string, string>,
  existingRowLabels: Set<string>,
  existingColumnLabels: Set<string>,
): VariableData[] {
  if (hiddenRowLabels.size === 0 && hiddenColumnLabels.size === 0) {
    return data;
  }

  return data
    .filter(
      (variable) =>
        !hiddenRowLabels.has(variable.variableLabel) ||
        existingRowLabels.has(variable.variableLabel),
    )
    .map((variable) => {
      if (hiddenColumnLabels.size === 0) return variable;
      return {
        ...variable,
        surveys: variable.surveys.map((survey) => ({
          ...survey,
          descriptiveMetrics: survey.descriptiveMetrics?.filter(
            (metric) =>
              !hiddenColumnLabels.has(metric.crossVariableLabel) ||
              existingColumnLabels.has(metric.crossVariableLabel),
          ),
        })),
      };
    });
}

/**
 * Transform crosstab API response data into table format
 *
 * @param data - Array of VariableData from API response
 * @returns Array of row objects suitable for CrosstabTable component
 *
 * @example
 * Input: [
 *   {
 *     variableId: "9",
 *     variableLabel: "Q1 2024",
 *     surveys: [{
 *       descriptiveMetrics: [
 *         { value: 100, baseN: 662, crossVariableLabel: "25", suppressed: false },
 *         { value: 100, baseN: 1669, crossVariableLabel: "26", suppressed: false }
 *       ]
 *     }]
 *   }
 * ]
 *
 * Output: [
 *   { id: 1, label: "Q1 2024", "25": 100, "26": 100 }
 * ]
 */
export function transformCrosstabData(
  data: VariableData[],
  decimals = 0,
  minimumBaseSize = 0,
): Array<Record<string, any>> {
  if (!data || data.length === 0) {
    return [];
  }

  return data.map((variable, index) => {
    const row: Record<string, any> = {
      id: index + 1,
      label: variable.variableLabel,
      _metrics: {}, // Store metrics separately for easy access
    };

    // Get descriptive metrics from the first survey
    const descriptiveMetrics = variable.surveys[0]?.descriptiveMetrics;

    if (descriptiveMetrics) {
      descriptiveMetrics.forEach((metric) => {
        // Use crossVariableLabel as column key
        // Check if suppressed by API or by minimum base size
        const isSuppressed =
          metric.suppressed || metric.baseN < minimumBaseSize;

        if (isSuppressed) {
          row[metric.crossVariableLabel] = "-";
        } else {
          // Format the value with specified decimal places
          row[metric.crossVariableLabel] = Number(
            metric.value.toFixed(decimals),
          );
        }

        // Store the full metric object for this column
        row._metrics[metric.crossVariableLabel] = {
          statSignificance: metric.statSignificance,
          rowStatSignificance: metric.rowStatSignificance,
          refColumnStatSignificance: metric.refColumnStatSignificance,
          baseN: metric.baseN,
        };
      });
    }

    // CRITICAL: Remove any request parameters that might have been echoed back by the API
    // These should never be treated as data columns
    delete row.enableStatTesting;
    delete row.enableRowStatTesting;

    return row;
  });
}

/**
 * Extract N values for each column from crosstab data
 *
 * @param data - Array of VariableData from API response
 * @returns Object mapping column labels to their N values
 *
 * @example
 * Input: [{ surveys: [{ descriptiveMetrics: [{ crossVariableLabel: "25", baseN: 662 }] }] }]
 * Output: { "25": 662 }
 */
export function extractColumnNValues(
  data: VariableData[],
): Record<string, number | string> {
  if (!data || data.length === 0) {
    return {};
  }

  const nValues: Record<string, number | string> = {};

  // Scan ALL variables to capture columns that may not appear in every row
  data.forEach((variable) => {
    const descriptiveMetrics = variable?.surveys[0]?.descriptiveMetrics;

    if (descriptiveMetrics) {
      descriptiveMetrics.forEach((metric) => {
        // Only set N value if we haven't seen this column yet
        if (!(metric.crossVariableLabel in nValues)) {
          if (metric.suppressed) {
            nValues[metric.crossVariableLabel] = "-";
          } else {
            nValues[metric.crossVariableLabel] = metric.baseN;
          }
        }
      });
    }
  });

  return nValues;
}

/**
 * Get unique column labels from crosstab data
 *
 * @param data - Array of VariableData from API response
 * @returns Array of unique column labels (crossVariableLabels)
 */
export function getCrosstabColumns(data: VariableData[]): string[] {
  if (!data || data.length === 0) {
    return [];
  }

  const columnSet = new Set<string>();

  data.forEach((variable) => {
    const descriptiveMetrics = variable.surveys[0]?.descriptiveMetrics;
    if (descriptiveMetrics) {
      descriptiveMetrics.forEach((metric) => {
        if (!metric.suppressed) {
          columnSet.add(metric.crossVariableLabel);
        }
      });
    }
  });

  return Array.from(columnSet).sort((a, b) => {
    // Sort numerically if both are numbers
    const numA = parseInt(a);
    const numB = parseInt(b);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    // Otherwise sort alphabetically
    return a.localeCompare(b);
  });
}

/**
 * Format crosstab value for display
 *
 * @param value - The value to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted string
 */
export function formatCrosstabValue(
  value: number | string,
  decimals = 0,
): string {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return value.toFixed(decimals);
  }

  return "-";
}
